<template>
  <div class="container">
    <div v-if="loading && !event">
      <spinner />
    </div>
    <div v-else>
      <link v-if="customStyles.stylesheet" rel="stylesheet" :href="customStyles.stylesheet">
      <div v-if="event.applicationsOpen">
        <div v-if="!order" role="status">
          <waiting-room v-if="conferenceWaitingRoom && conferenceWaitingRoom.status === 'WAITING_ROOM'" :event-title="event.title" :position-in-queue="conferenceWaitingRoom.positionInQueue" :people-in-front="conferenceWaitingRoom.peopleInFront" />
          <p v-else-if="conferenceWaitingRoom && conferenceWaitingRoom.status === 'OK'">{{ $t('waitingRoomYourTurn') }}</p>
          <spinner v-if="conferenceCreatePending" />
          <p v-if="conferenceCreateAmbiguous">{{ $t('registerRefreshFailed') }}</p>
          <p v-if="conferenceReservationExpired">{{ $t('reservationExpired') }}</p>
          <button v-if="conferenceReservationExpired" type="button" @click="startNewConferenceReservation">{{ $t('registerBuyMoreTickets') }}</button>
          <button v-else type="button" :disabled="conferenceCreatePending" @click="onTicketsSelected">{{ $t(conferenceWaitingRoom && conferenceWaitingRoom.status === 'OK' ? 'buyATicket' : 'tryAgain') }}</button>
        </div>
        <div
          v-if="!showMessage && order"
          class="grid-container"
          :style="
            event.extraModules.ticketing ? 'justify-content:flex-end' : 'justify-content:center'
          "
        >
          <div v-if="event.cover && (activeStep === 1 || activeStep === 4)" class="event-cover">
            <img :src="event.cover" alt="cover image">
          </div>
          <v-flex v-else-if="activeStep === 1">
            <card-header :event="event" />
          </v-flex>

          <main :class="[activeStep !== 1 && 'main-row', !event.cover && 'main-no-image']">
            <div class="ticket-list-container">
              <checkout
                ref="checkoutRef"
                :event="event"
                :schema="{validationSchema: {}}"
                :styles="{ ...customStyles.attendeeForm, goBack: $t('cancel') }"
                :initial-values="form.attendee"
                :selected-tickets="shoppingCart"
                :total-order-price="totalPrice()"
                :ticket-insurance-amount="ticketInsuranceAmount"
                hide-buyer-fields
                @user="user = $event"
                @changeInsurance="ticketsInsurance = $event"
                @back="cancelOrder()"
                @next="onCheckoutNext"
              />
            </div>
          </main>
          <card-header-cover
            v-if="event.cover || activeStep !== 1"
            :style="{
              backgroundColor: getThemeStyle(event.coverImagePrimaryColor, 0.05),
            }"
            class="overflow-card"
            :event="event"
            :active-step="activeStep"
          />
          <aside v-if="event.extraModules.ticketing" class="cover-header-style">
            <ticket-price-list
              v-if="event.extraModules.ticketing && shoppingCart.length !== 0"
              ref="ticketPriceList"
              :selected-tickets="shoppingCart"
              :cover="event.cover"
              :promo-code="promoCode"
              :event="event"
              :shopping-cart="shoppingCart"
              :start-time="startTime"
              :user="user"
              :selected-ticket-insurance="ticketsInsurance"
              hide-edit-button
              @signIn="scrollToLoginOptions"
              @triggerPayment="handlePaymentClick"
              @updateTicketInsuranceAmount="ticketInsuranceAmount = $event"
            />
            <checkout-timer
              v-if="event.extraModules.ticketing && shoppingCart.length !== 0 && activeStep !== 1"
              :event="event"
              :start-time="startTime"
            />
          </aside>
        </div>
        <v-layout v-else-if="showMessage" justify-center>
          <v-flex shrink class="registration-success-page">
            <img
              class="info-image"
              src="../../../../public/assets/mail_success.svg"
              alt="mail_successful"
            >
            <div class="my-page-title">
              <span v-if="customStyles.base && customStyles.base.submitNote">{{
                customStyles.base.submitNote
              }}</span>
              <span v-else>{{ $t('registrationFinish') }}</span>
            </div>
            <div v-if="customStyles.base && customStyles.base.submitInfo" class="description">
              {{ customStyles.base.submitInfo }}
            </div>
            <div v-else class="description">
              {{ $t('registrationDescription') }}
            </div>
            <div class="mt-12 text-center">
              <main-button
                :text="(customStyles.base && customStyles.base.addAnother) || $t('addAttendee')"
                @click="addAnother"
              />
            </div>
          </v-flex>
        </v-layout>
        <simple-snackbar :open.sync="showError" color="primary" :message-prop="snackbarMessage" />
      </div>
      <v-flex v-else xs12>
        <v-layout wrap justify-center style="height: 100%">
          <v-flex shrink class="registration-success-page">
            <img class="info-image" src="../../../../public/assets/sad_cloud.svg" alt="sad_cloud">
            <h1 v-if="customStyles.base && customStyles.base.eventClosed" class="my-page-title">
              {{ customStyles.base.eventClosed }}
            </h1>
            <h1 v-else class="my-page-title">{{ $t('aplicationsClosed') }}</h1>
          </v-flex>
        </v-layout>
      </v-flex>
      <simple-snackbar
        :open.sync="snackbarOpen"
        :message-prop="$t('aplicationSuccessful')"
        color="primary"
      />
    </div>
  </div>
</template>

<script>
import { GET_EVENT_BY_SLUG } from '../../../graphql/event.graphql.js'
import CardHeader from '../../shared/card-header.vue'
import Spinner from './../../shared/spinner'
import CheckoutTimer from '../register-stepper/checkout-timer.vue'
import { isRevolutCheckoutActive } from '@/revolut-checkout-state'
import {
  createPurchaseAccreditationsPublicConferenceTicketing,
  getOrderInfoByUUID,
  populateOrderDataConferenceTicketing,
  cancelOrderConferenceTicketing,
} from '../../../http.js'
import SimpleSnackbar from '../../shared/snackbars/simple-snackbar.vue'
import {
  submitGuard,
  createAttendeeSchema,
  initializeRegisterForm,
  createCustomPaymentCheckout,
  startPaymentCheckout,
  landingURL,
} from '../../../utils'
import CardHeaderCover from '../../shared/card-header-cover.vue'
import TicketPriceList from '../register-stepper/ticket-price-list.vue'
import Checkout from '../register-stepper/checkout.vue'
import moment from 'moment'
import i18next from 'i18next'
import axios from 'axios'
import { serverURL } from '@/utils'
import { waitingRoomHeaders, rememberWaitingRoomSession, clearWaitingRoomSession, waitingRoomErrorCode } from '@/utils/waiting-room-session'
import WaitingRoom from './waiting-room.vue'
import { readReservationRequest, createReservationRequest, clearReservationRequest, isReservationRejected, isReservationExpired } from '@/utils/reservation-request'

export default {
  name: 'custom-register',
  components: {
    Spinner,
    CardHeader,
    SimpleSnackbar,
    CardHeaderCover,
    TicketPriceList,
    Checkout,
    CheckoutTimer,
    WaitingRoom,
  },
  props: {
    eventSlug: {
      type: String,
      default: null,
    },
    attendeeUUID: {
      type: String,
      default: null,
    },
    status: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    showMessage: false,
    activeStep: 1,
    event: null,
    form: {
      attendee: {},
      workshops: [],
    },
    showError: false,
    customStyles: {},
    schema: {},
    orderUUID: null,
    initialPromoCode: null,
    snackbarOpen: false,
    snackbarMessage: '',
    initialTicketId: [],
    initialAttendee: {},
    loading: true,
    coverColor: {},
    backgroundCoverColor: {},
    shoppingCart: [],
    promoCode: null,
    order: null,
    startTime: null,
    user: null,
    ticketsInsurance: false,
    ticketInsuranceAmount: null,
    submitting: false,
    conferenceCreatePending: false,
    conferenceCreateAmbiguous: false,
    conferenceReservationRequest: null,
    conferenceReservationExpired: false,
    conferenceWaitingRoom: null,
    conferenceWaitingTimer: null,
    conferenceHeartbeatTimer: null,
  }),
  computed: {
    referralName() {
      return this.$route.query.ref
    },
  },
  apollo: {
    getEventBySlug: {
      query: GET_EVENT_BY_SLUG,
      variables() {
        return {
          slug: this.eventSlug,
        }
      },
      manual: true,
      async result({ loading, data }) {
        if (!loading) {
          const { getEventBySlug: event } = data
          this.event = event
          if (this.event.translation) {
            for (const translation in this.event.translation.translationsJSON) {
              i18next.addResourceBundle(
                translation,
                'default',
                this.event.translation.translationsJSON[translation],
                true,
                false,
              )
            }
          }
          this.customStyles =
            (event.eventUtils &&
              event.eventUtils.inviteFormStyles &&
              event.eventUtils.inviteFormStyles) ||
            {}
          this.schema = createAttendeeSchema(event.attendeeFields)

          this.initialPromoCode = this.$route.query.promo
          // oiid = comma separated ticket ids from the landing page; without it there is no cart
          this.initialTicketId = (this.$route.query.oiid || '').split(',').filter(Boolean)
          if (!this.initialTicketId.length) {
            this.showErrorMessage(this.$t('membershipRequestErrorDescription'))
            this.loading = false

            return
          }
          const item = await this.parseSavedOrder(event.id)
          if (item && item[0]) {
            const [savedOrder, startedAt] = item
            this.order = savedOrder
            this.activeStep = 2
            const form = await initializeRegisterForm(savedOrder)
            this.form = form[0]
            this.shoppingCart = form[1]
            this.startTime = new Date(startedAt)
          }
          if (!item) {
            await this.onTicketsSelected()
          }
          this.loading = false
        }
      },
    },
  },

  beforeDestroy() {
    clearTimeout(this.conferenceWaitingTimer)
    this.stopConferenceHeartbeat()
  },
  methods: {
    stopConferenceHeartbeat() {
      clearInterval(this.conferenceHeartbeatTimer)
      this.conferenceHeartbeatTimer = null
    },
    async sendConferenceHeartbeat() {
      try {
        await axios.post(`${serverURL}/register/heartbeat`, {}, { withCredentials: true, headers: waitingRoomHeaders(), timeout: 15000 })
      } catch (error) {
        if (this._isDestroyed || !error.response || error.response.status !== 400) return
        this.stopConferenceHeartbeat()
        clearWaitingRoomSession()
        await this.checkConferenceAdmission()
      }
    },
    async checkConferenceAdmission() {
      clearTimeout(this.conferenceWaitingTimer)
      try {
        const { data } = await axios.post(`${serverURL}/register/check-in`, {}, { withCredentials: true, headers: waitingRoomHeaders(), timeout: 15000 })
        if (this._isDestroyed) return false
        rememberWaitingRoomSession(data)
        this.conferenceWaitingRoom = data
        if (data.status === 'OK') {
          if (!this.conferenceHeartbeatTimer) this.conferenceHeartbeatTimer = setInterval(() => this.sendConferenceHeartbeat(), 30000)

          return true
        }
      } catch (error) {
        if (error.response && error.response.status === 400) clearWaitingRoomSession()
        this.conferenceWaitingRoom = { status: 'WAITING_ROOM' }
      }
      this.stopConferenceHeartbeat()
      if (!this._isDestroyed) this.conferenceWaitingTimer = setTimeout(() => this.checkConferenceAdmission(), 30000)

      return false
    },
    scrollToLoginOptions() {
      if (this.$refs.checkoutRef) {
        this.$refs.checkoutRef.scrollToLoginOptions()
      }
    },
    async cancelOrder() {
      await cancelOrderConferenceTicketing({
        orderUUID: this.orderUUID,
      })
      localStorage.removeItem('order')
      window.open(`${landingURL}`, '_self')
    },
    async parseSavedOrder(eventId) {
      const savedOrder = localStorage.getItem('order')
      if (!savedOrder) {
        return null
      }
      let order
      try {
        order = JSON.parse(savedOrder)
      } catch (e) {
        return null
      }
      const { orderUUID, startedAt, attendeeUUID, oiid } = order
      // Only resume an order started from this same registration link (same attendee and
      // tickets); otherwise a second registration on this device would get the first one's cart.
      if (attendeeUUID !== this.attendeeUUID || oiid !== this.initialTicketId.join(',')) {
        return null
      }
      const differenceInMinutes = moment(new Date()).diff(moment(startedAt), 'minutes')
      if (differenceInMinutes >= 15 && !isRevolutCheckoutActive()) {
        localStorage.removeItem('order')

        return null
      }
      const found = await getOrderInfoByUUID(orderUUID)
      if (!found || found.eventId !== eventId) {
        return null
      }
      this.orderUUID = orderUUID

      return [found, startedAt]
    },
    startNewConferenceReservation() {
      const scope = `conference:${this.event.id}:${this.attendeeUUID}`
      if (this.conferenceReservationRequest) clearReservationRequest(scope, this.conferenceReservationRequest)
      this.conferenceReservationRequest = null
      this.conferenceReservationExpired = false
      this.conferenceCreateAmbiguous = false

      return this.onTicketsSelected()
    },
    conferenceReservationFinished(order) {
      if ((!order.status || order.status === 'PURCHASING') && order.canResumeReservationCheckout !== false) return false
      this.order = order
      if (this.conferenceReservationRequest) clearReservationRequest(`conference:${this.event.id}:${this.attendeeUUID}`, this.conferenceReservationRequest)
      this.conferenceReservationRequest = null
      this.$router.push({ path: `${['SUCCESSFUL', 'PURCHASING'].includes(order.status) ? '/custom-successful-payment/' : '/canceled-payment/'}${order.uuid}` })

      return true
    },
    async recoverConferenceReservation(request) {
      let order
      try { order = await getOrderInfoByUUID(request.requestUUID) } catch (_) { return false }
      if (!order || order.uuid !== request.requestUUID || String(order.eventId) !== String(this.event.id)) return false
      this.initialTicketId = [...request.variables.attendeeTicketId]
      if (this.conferenceReservationFinished(order)) return true
      const [, cart] = await initializeRegisterForm(order)
      if (!Array.isArray(cart)) throw new Error('Saved reservation unavailable')
      this.orderUUID = order.uuid
      this.order = order
      this.shoppingCart = cart
      this.form.attendee.tickets = cart
      this.startTime = new Date(order.createdAt)
      this.activeStep = 2
      this.conferenceCreateAmbiguous = false
      clearTimeout(this.conferenceWaitingTimer)
      this.stopConferenceHeartbeat()

      return true
    },
    async onTicketsSelected() {
      if (this.conferenceCreatePending || this.conferenceReservationExpired || this.order) return
      this.conferenceCreatePending = true
      try {
        // A committed UUID is a recovery read, never another order creation.
        if (this.orderUUID) {
          this.stopConferenceHeartbeat()
          clearTimeout(this.conferenceWaitingTimer)
          this.order = await getOrderInfoByUUID(this.orderUUID)

          return
        }
        const requestScope = `conference:${this.event.id}:${this.attendeeUUID}`
        let request = this.conferenceReservationRequest || readReservationRequest(requestScope)
        if (request) {
          this.conferenceReservationRequest = request
          this.initialTicketId = [...request.variables.attendeeTicketId]
          if (await this.recoverConferenceReservation(request)) return
        }
        if (!await this.checkConferenceAdmission() || this._isDestroyed) return
        const variables = {
          eventId: this.event.id,
          attendeeTicketId: this.initialTicketId,
          attendeeUUID: this.attendeeUUID,
          buyer: { fields: {} },
        }
        request = request || createReservationRequest(requestScope, variables)
        this.conferenceReservationRequest = request
        let response
        try {
          response = await createPurchaseAccreditationsPublicConferenceTicketing({ ...request.variables, requestUUID: request.requestUUID })
        } catch (error) {
          if (waitingRoomErrorCode(error)) {
            await this.checkConferenceAdmission()

            return
          }
          if (isReservationExpired(error)) this.conferenceReservationExpired = true
          if (isReservationRejected(error)) {
            clearReservationRequest(requestScope, request)
            this.conferenceReservationRequest = null
            this.conferenceCreateAmbiguous = false
          } else this.conferenceCreateAmbiguous = true
          throw error
        }
        if (waitingRoomErrorCode(response)) {
          await this.checkConferenceAdmission()

          return
        }
        if (isReservationExpired(response)) {
          this.conferenceReservationExpired = true
          throw new Error(this.$t('reservationExpired'))
        }
        if (isReservationRejected(response)) {
          clearReservationRequest(requestScope, request)
          this.conferenceReservationRequest = null
          this.conferenceCreateAmbiguous = false
          throw new Error(response.errors[0].message)
        }
        const created = response && !response.errors && response.data && response.data.createPurchaseAccreditationsPublicConferenceTicketing
        if (!created || !created.createdOrder || !created.createdOrder.uuid) {
          this.conferenceCreateAmbiguous = true
          throw new Error('Conference order result unavailable')
        }
        this.orderUUID = created.createdOrder.uuid
        if (this.conferenceReservationFinished(created.createdOrder)) return
        this.conferenceCreateAmbiguous = false
        this.shoppingCart = created.createdAccreditations.map(a => created.orderItem.find(oi => oi.id == a.ticketId))
        this.form.attendee.tickets = this.shoppingCart
        this.startTime = new Date(created.createdOrder.createdAt)
        try { localStorage.setItem('order', JSON.stringify({
          orderUUID: this.orderUUID,
          startedAt: this.startTime,
          attendeeUUID: this.attendeeUUID,
          oiid: this.initialTicketId.join(','),
        })) } catch (error) { /* Keep the committed UUID in memory when browser storage is blocked. */ }
        this.activeStep = 2
        clearTimeout(this.conferenceWaitingTimer)
        this.stopConferenceHeartbeat()
        // New-order admission ends here. Paying the existing held order does not need a slot.
        await axios.post(`${serverURL}/register/check-out`, {}, { withCredentials: true, headers: waitingRoomHeaders(), timeout: 15000 }).catch(() => {})
        this.order = await getOrderInfoByUUID(this.orderUUID)
      } catch (error) {
        this.showErrorMessage(this.$t('membershipRequestErrorDescription'))
      } finally {
        this.conferenceCreatePending = false
      }
    },
    showErrorMessage(message) {
      this.snackbarMessage = message
      this.showError = true
    },
    setSubmitting(value) {
      this.submitting = value
      if (this.$refs.ticketPriceList) {
        this.$refs.ticketPriceList.disabledSubmitButton = value
      }
    },
    async purchaseTicket(paymentType) {
      if (isRevolutCheckoutActive()) return
      if (this.totalPriceNum() > 0 && !paymentType) return
      // Confirm can be clicked twice (price list bar + checkout); only the first click counts
      if (this.submitting) {
        return
      }
      const timeDiff = moment.duration(moment().diff(moment(this.startTime)))
      if (timeDiff.asMinutes() >= 15) {
        localStorage.removeItem('order')
        window.location.reload()

        return
      }
      this.setSubmitting(true)
      const {
        attendee: { tickets, ...rest },
      } = this.form
      rest.getSubscription = rest.getSubscription || false
      const { purchases } = this.order
      const variables = {
        eventId: this.event.id,
        orderId: this.order.id,
        buyer: { fields: rest },
        attendees: purchases.map(p => ({
          fields: {
            'First name': rest.firstName,
            'Last name': rest.lastName,
            Email: rest.email,
          },
          id: p.attendeeId,
        })),
        // Checkout includes insurance; the backend stores the same amount
        ticketInsurance: this.ticketsInsurance && this.totalPriceNum() > 0,
      }
      let populated = null
      try {
        populated = await populateOrderDataConferenceTicketing(variables)
      } catch (e) {
        populated = null
      }
      const populatedOrder = populated && populated.data && populated.data.populateOrderDataConferenceTicketing
      if (!populatedOrder || populated.errors) {
        this.setSubmitting(false)
        this.showErrorMessage(this.$t('paymentUnsuccessfulDescription'))

        return
      }

      localStorage.removeItem('order')

      if (this.totalPriceNum() === 0) {
        return this.$router.push({
          name: 'successful-payment',
          params: { orderUUID: this.order.uuid },
        })
      }
      this.form.paymentMethod = paymentType
      try {
        const checkout = await createCustomPaymentCheckout(
          this.form,
          this.totalPrice(),
          this.order,
          this.shoppingCart,
          this.event.id,
        )
        const result = await startPaymentCheckout(checkout)
        if (result && ['FAILED', 'CANCELED', 'REFUNDED'].includes(result.status)) {
          this.setSubmitting(false)
        }
      } catch (e) {
        this.setSubmitting(false)
        this.showErrorMessage(this.$t('paymentUnsuccessfulDescription'))
      }
    },
    totalPriceNum() {
      if (this.$refs.ticketPriceList) {
        return this.$refs.ticketPriceList.calculateTotalPrice()
      }

      return 0
    },
    totalPrice() {
      if (this.$refs.ticketPriceList) {
        return this.$refs.ticketPriceList.getTotalPrice()
      }

      return '0 EUR'
    },
    goBack() {
      this.$router.push({
        name: 'event-public',
        params: { slug: this.event.slug },
      })
    },
    handlePaymentClick() {
      this.$refs.checkoutRef.triggerButtonClick()
    },
    onCheckoutNext(event) {
      const { paymentType } = event
      this.form.attendee = {
        zones: [],
        country: event['country'],
        firstName: event['First name'],
        lastName: event['Last name'],
        email: event['Email'],
      }
      this.purchaseTicket(paymentType)
    },
  },
}
</script>

<style scoped>
.container {
  min-height: 100%;
  min-width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0;
}

.card-header-container {
  margin-bottom: 48px;
}

.event-cover {
  background-size: cover;
  border-radius: 8px 8px 0 0;
  font-size: 0;
  display: flex;
}

@supports not (aspect-ratio: 16/9) {
  .event-cover {
    position: relative;
    height: 0;
    padding-top: 33.33333%;
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

.event-cover img {
  aspect-ratio: 16/9;
  object-fit: cover;
  width: 100%;
  border-radius: 8px 8px 0 0;
}

.cover-header-noTicket {
  margin: 150px 0 0 50px;
  height: auto;
}

.my-page-title {
  font-family: Raleway, serif;
  font-style: normal;
  font-weight: bold;
  text-align: center;
  font-size: 38px;
  line-height: 45px;
  color: #ff4c00;
  margin-bottom: 24px;
  margin-top: 60px;
}

.description {
  font-family: Source Sans Pro, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 23px;
  text-align: center;
  color: #333333;
}

.card-header-container {
  margin-bottom: 48px;
}

.registration-success-page {
  max-width: 640px !important;
  align-items: center;
  margin: 120px 20px 0 20px;
  display: flex;
  flex-direction: column;
}

.info-image {
  width: 100%;
}

.ticket-list-container {
  margin-right: 30px;
}

/* layout */

.grid-container::before {
  content: '';
  grid-row: 1/-1;
}

.grid-container {
  max-width: 1440px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 366px) minmax(50%, 692px) minmax(25%, 366px);
  grid-template-rows: min-content min-content max-content;
}

/* .event-content-container {
  grid-column: 3;
  grid-row: 1;
  display: flex;
  flex-direction: column;
} */

.event-cover {
  grid-column: 3;
  grid-row: 1;
}

.overflow-card {
  grid-column: 3;
}

.cover-header-style {
  grid-column: 3;
}

main {
  grid-column: 2 / 3;
  grid-row: 1 / -1;
}

.main-no-image {
  grid-column: 2 / 3;
}

@media screen and (max-width: 992px) and (min-width: 801px) {
  .main-no-image {
    margin: 0 1.5rem;
  }

  /* checkout bleeds -3.5rem sideways at this width; this padding (+ main's 1.5rem margin
     when there is no cover) absorbs it, otherwise the page scrolls horizontally on tablets */
  .ticket-list-container {
    padding: 0 3.5rem;
  }

  .main-no-image .ticket-list-container {
    padding: 0 2rem;
  }
}

@media screen and (max-width: 992px) {
  .event-cover {
    display: none !important;
    grid-column: 1;
  }

  .register-layout {
    flex-direction: column-reverse;
  }

  .cover-header-noTicket {
    margin: 0;
  }

  .grid-container {
    grid-template-columns: 1fr;
  }

  .overflow-card {
    grid-column: 1;
    grid-row: 1;
  }

  main {
    grid-column: 1;
    grid-row: 2;
  }

  .main-no-image {
    grid-column: 1;
    grid-row: 2;
  }

  .cover-header-style {
    margin: 0;
    grid-column: 1;
  }

  .ticket-list-container {
    margin-right: 0;
  }
}

.no-tickets {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 3rem;
}

.no-tickets img {
  margin: 64px 0;
}
</style>
